import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ConfigContext } from '../shared/ConfigContext';
import CouponClient from '../../api/couponClient';
import { usePlanLimitContent } from '../help/planLimitContent';

const PAGE_SIZE = 25;
const DEFAULT_COLOR = '#6366f1';

const emptyForm = (companyId) => ({
  code: '',
  internalDescription: '',
  color: DEFAULT_COLOR,
  discountType: 'PERCENT',
  discountValue: '',
  applicableProducts: [],
  eligibleUsers: [],
  expiryDate: null,
  neverExpires: true,
  companyId,
});

// Same accumulating "load more" fetch pattern as useProductsPage/useCategoriesPage.
// Active/Expired chips filter the already-loaded window client-side — expiry
// state isn't a stored/queryable field, so this mirrors the old page's exact
// filtering behaviour rather than adding a new backend filter param.
const useCouponsPage = () => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const [searchParams] = useSearchParams();
  const previewState = searchParams.get('previewState');
  const planLimit = usePlanLimitContent('coupons.active.max');

  const [companyId] = useState(() => localStorage.getItem('companyId') || '');

  const [rows, setRows] = useState([]);
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filterTab, setFilterTab] = useState('all');

  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardMode, setWizardMode] = useState('create');
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(emptyForm(companyId));
  const initialFormRef = useRef(form);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const fetchCoupons = useCallback((reset = true) => {
    if (!config?.backendUrl || !companyId) return;
    if (reset) setStatus('loading'); else setLoadingMore(true);
    const page = reset ? 1 : Math.floor(rowsRef.current.length / PAGE_SIZE) + 1;
    const params = { companyId, page, pageSize: PAGE_SIZE };
    if (search) params.search = search;
    axios.get(`${config.backendUrl}/coupons`, { params })
      .then((res) => {
        setRows((prev) => (reset ? res.data.items : [...prev, ...res.data.items]));
        setTotal(res.data.total);
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error fetching coupons', error);
        if (error.response?.status === 403) {
          setStatus('permission');
        } else {
          setErrorInfo({
            request: `GET /coupons · ${error.response?.status || t('N/A')} · ${new Date().toLocaleTimeString()}`,
          });
          setStatus('error');
        }
      })
      .finally(() => setLoadingMore(false));
  }, [config, companyId, search, t]);

  useEffect(() => { fetchCoupons(true); }, [fetchCoupons]);

  useEffect(() => {
    if (!config?.backendUrl || !companyId) return;
    axios.get(`${config.backendUrl}/products`, { params: { companyId } })
      .then((res) => setProducts(res.data))
      .catch((error) => console.error('Error fetching products', error));
    axios.get(`${config.backendUrl}/users`, { params: { companyId } })
      .then((res) => setUsers(res.data))
      .catch((error) => console.error('Error fetching users', error));
  }, [config, companyId]);

  const loadMore = () => fetchCoupons(false);
  const remaining = Math.max(0, total - rows.length);
  const nextBatchSize = Math.min(PAGE_SIZE, remaining);

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  // ---- Create / edit wizard ---------------------------------------------//
  const openCreateWizard = () => {
    const fresh = emptyForm(companyId);
    setWizardMode('create');
    setForm(fresh);
    initialFormRef.current = fresh;
    setEditingId(null);
    setActiveStep(0);
    setWizardOpen(true);
  };

  const openEditWizard = (coupon) => {
    const loaded = {
      code: coupon.code,
      internalDescription: coupon.internalDescription || '',
      color: coupon.color || DEFAULT_COLOR,
      discountType: coupon.discountType,
      discountValue: String(coupon.discountValue ?? ''),
      applicableProducts: coupon.applicableProducts || [],
      eligibleUsers: coupon.eligibleUsers || [],
      expiryDate: coupon.expiryDate || null,
      neverExpires: !coupon.expiryDate,
      companyId,
    };
    setWizardMode('edit');
    setForm(loaded);
    initialFormRef.current = loaded;
    setEditingId(coupon._id);
    setActiveStep(0);
    setWizardOpen(true);
  };

  const closeWizard = () => {
    if (saving) return;
    setWizardOpen(false);
  };

  const isDirty = JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

  const toggleProduct = (product) => {
    const isSelected = form.applicableProducts.some((p) => p._id === product._id);
    setForm({
      ...form,
      applicableProducts: isSelected
        ? form.applicableProducts.filter((p) => p._id !== product._id)
        : [...form.applicableProducts, product],
    });
  };

  const toggleUser = (user) => {
    const isSelected = form.eligibleUsers.some((u) => u._id === user._id);
    setForm({
      ...form,
      eligibleUsers: isSelected
        ? form.eligibleUsers.filter((u) => u._id !== user._id)
        : [...form.eligibleUsers, user],
    });
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const client = new CouponClient(config.backendUrl);
      const payload = {
        code: form.code,
        internalDescription: form.internalDescription,
        color: form.color,
        discountType: form.discountType,
        discountValue: parseFloat(form.discountValue),
        applicableProductIds: form.applicableProducts.map((p) => p._id),
        eligibleUserIds: form.eligibleUsers.map((u) => u._id),
        expiresAt: form.neverExpires ? null : form.expiryDate,
        companyId,
      };
      if (wizardMode === 'create') {
        await client.createCoupon(payload);
        notify(t('Coupon created successfully'));
      } else {
        await client.updateCoupon(editingId, payload);
        notify(t('Coupon updated successfully'));
      }
      setWizardOpen(false);
      fetchCoupons(true);
    } catch (error) {
      console.error('Error saving coupon', error);
      notify(wizardMode === 'create' ? t('Failed to create coupon') : t('Failed to update coupon'), 'error');
    } finally {
      setSaving(false);
    }
  };

  // ---- Delete confirm -----------------------------------------------------//
  const openDeleteConfirm = (coupon) => {
    setConfirmTarget(coupon);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!confirmTarget) return;
    setDeleting(true);
    try {
      const client = new CouponClient(config.backendUrl);
      await client.deleteCoupon(confirmTarget._id);
      notify(t('Coupon deleted successfully'));
      setConfirmOpen(false);
      fetchCoupons(true);
    } catch (error) {
      console.error('Error deleting coupon', error);
      notify(t('Failed to delete coupon'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const mappedRows = rows.map((coupon) => ({
    id: coupon._id,
    _id: coupon._id,
    code: coupon.code,
    color: coupon.color,
    internalDescription: coupon.internalDescription,
    discountType: coupon.discountType,
    discountValue: coupon.discountValue,
    applicableProducts: coupon.applicableProductIds || [],
    eligibleUsers: coupon.eligibleUserIds || [],
    expiryDate: coupon.expiresAt,
  }));

  const filteredRows = mappedRows.filter((coupon) => {
    if (filterTab === 'active' && isExpired(coupon.expiryDate)) return false;
    if (filterTab === 'expired' && !isExpired(coupon.expiryDate)) return false;
    return true;
  });

  const isPreviewEmptyKind = previewState === 'empty' || previewState === 'no-match';
  const effectiveStatus = isPreviewEmptyKind ? 'ready' : (previewState || status);
  const effectiveRows = isPreviewEmptyKind ? [] : (effectiveStatus === 'ready' ? filteredRows : []);
  const isNoMatch = isPreviewEmptyKind ? previewState === 'no-match' : Boolean(search);

  return {
    t, companyId, total, errorInfo, effectiveStatus, effectiveRows, isNoMatch, search,
    searchInput, setSearchInput, setSearch, filterTab, setFilterTab,
    loadingMore, loadMore, remaining, nextBatchSize, fetchCoupons, isExpired,
    products, users,
    wizardOpen, wizardMode, activeStep, setActiveStep, form, setForm, saving, isDirty,
    openCreateWizard, openEditWizard, closeWizard, handleSave, toggleProduct, toggleUser,
    confirmOpen, setConfirmOpen, confirmTarget, deleting, openDeleteConfirm, handleConfirmDelete,
    snackbar, setSnackbar,
    planLimit,
  };
};

export default useCouponsPage;
